/**
 * SupportWidget - Recent customer support tickets widget for admin dashboard
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import api from '../lib/api';
import { 
  Headphones, 
  ChevronRight, 
  Package, 
  CreditCard, 
  AlertTriangle,
  HelpCircle,
  MessageCircle,
  Loader2,
  RefreshCw
} from 'lucide-react';

// Status colors
const STATUS_COLORS = {
  open: { bg: 'bg-yellow-500', text: 'text-black', label: 'Open' },
  in_progress: { bg: 'bg-emerald-500', text: 'text-white', label: 'In Progress' },
  resolved: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Resolved' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Closed' }
};

// Priority colors
const PRIORITY_COLORS = {
  low: 'text-gray-500',
  medium: 'text-blue-500',
  high: 'text-orange-500',
  critical: 'text-red-500'
};

// Category icons
const CATEGORY_ICONS = {
  delivery_issue: Package,
  payment_issue: CreditCard,
  fraud_suspicion: AlertTriangle,
  app_support: HelpCircle,
  other: MessageCircle
};

export default function SupportWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchRecentTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/support/tickets/recent?limit=5');
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load recent tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentTickets();
  }, []);

  const formatTimeAgo = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card className="bg-white border border-gray-100 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <Headphones className="h-5 w-5 text-emerald-500" />
          Customer Support
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={fetchRecentTickets}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Badge variant="outline" className="text-xs">
            {total} total
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Headphones className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>No support tickets yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => {
              const CategoryIcon = CATEGORY_ICONS[ticket.category] || MessageCircle;
              const status = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
              const priorityColor = PRIORITY_COLORS[ticket.priority] || 'text-gray-500';
              
              return (
                <div
                  key={ticket.id}
                  onClick={() => navigate('/admin/support')}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-2 rounded-full bg-white border border-gray-200">
                      <CategoryIcon className={`h-4 w-4 ${priorityColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">
                        {ticket.subject}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatTimeAgo(ticket.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${status.bg} ${status.text} text-xs px-2 py-0.5`}>
                      {status.label}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <Button
          onClick={() => navigate('/admin/support')}
          variant="outline"
          className="w-full mt-4 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
        >
          View All Support Tickets
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
}
